import React, { useState, useEffect, useRef, useMemo } from 'react'
import { array, object } from 'yup'
import { Formik, FormikActions } from 'formik'
import { Modal as ModalAnt, notification, Popover, Select, Tooltip } from 'antd'
import { useDispatch, useSelector } from 'react-redux'

import {
  BudgetChartTicket,
  FullRequestHotel,
  FullRequestHotelRooms,
  Id,
  IntegrationType,
  Request,
  RequestUserData,
  ResumeHotel
} from 'src/types'

import { Alert, Button, Input, Modal, Text, Title } from 'src/components/Ant'
import { ApplicationState } from 'src/store'
import { setHotelsNextPeriodKey } from 'src/core/store/ducks/hotels/actions'
import { useStepsContext } from 'src/views/Requests/views/Form'
import { useT } from 'src/core/hooks/use-translation'
import CurrencyMask from 'src/components/Formik/InputCurrency/mask'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import {
  DivCombo,
  DivRoomInfos,
  DivUserList,
  IconPension,
  OfflineOffer,
  WrapAbleUsers,
  WrapAvatar,
  WrapListItem
} from './styles'

const initialValues: SelectedHotel = {
  bedrooms: [],
  justification: ''
}

type Bedroom = {
  category: string
  key: number
  price: Id
  users: RequestUserData[]
  pension: boolean
  max_adults: number
  customer_supplier_id: Id
  integration_type: IntegrationType
}

export type SelectedHotel = {
  bedrooms: Bedroom[]
  justification: string
}

type Props = {
  item: ResumeHotel
  loading?: boolean
  onSubmit?(fullRequestHotel: FullRequestHotel[]): void
  request: Request
  selectedHotels: FullRequestHotel[]
  setSelectedHotels: (value: FullRequestHotel[]) => void
  toggle(): void
  visible: boolean
}

function ModalSelectedHotel({
  item,
  loading,
  onSubmit,
  request,
  toggle,
  visible,
  selectedHotels,
  setSelectedHotels
}: Props): JSX.Element {
  const {
    hotels: { searches, currentPeriodKey }
  } = useSelector((state: ApplicationState) => ({
    hotels: state.hotels
  }))

  const { t } = useT()
  const dispatch = useDispatch()

  const [users, setUsers] = useState<RequestUserData[]>([])
  const refFormik = useRef<Formik<SelectedHotel>>(null)

  const stepsContext = useStepsContext()
  const policyRule = useReasonPolicyRule(
    request.request_user_data[0]?.reason_id
  )

  const categoryList = useMemo(
    () =>
      !item
        ? []
        : item.request_quote_hotel_rooms.reduce(
            (acc, { category }) =>
              acc.includes(category) ? acc : [...acc, category],
            [] as string[]
          ),
    [item]
  )

  function getPriceList(category: string) {
    return item
      ? item.request_quote_hotel_rooms.filter(
          rooItem => rooItem.category === category
        )
      : []
  }

  function findRoomWithHash(request_quote_hotel_room_hash: string) {
    return item?.request_quote_hotel_rooms.find(
      rooItem =>
        rooItem.request_quote_hotel_room_hash === request_quote_hotel_room_hash
    )
  }

  useEffect(() => {
    let timeout2: null | ReturnType<typeof setTimeout> = null
    const timeout1 = setTimeout(() => {
      const arrBedrooms: Bedroom[] = []
      const selectedPeriodBedrooms =
        searches[currentPeriodKey]?.filters?.bedrooms

      if (!!item && selectedPeriodBedrooms) {
        for (let b = 0; b < selectedPeriodBedrooms; b += 1) {
          arrBedrooms.push({
            customer_supplier_id:
              item.request_quote_hotel_rooms[0].customer_supplier_id,
            integration_type:
              item.request_quote_hotel_rooms[0].integration_type,
            pension: item.request_quote_hotel_rooms[0].pension,
            max_adults: item.request_quote_hotel_rooms[0].max_adults,
            category: item.request_quote_hotel_rooms[0].category,
            key: b + 1,
            price:
              item.request_quote_hotel_rooms[0].request_quote_hotel_room_hash,
            users:
              request.request_user_data.length === 1
                ? [...request.request_user_data]
                : []
          })
        }
      }

      if (request.request_user_data.length === 1) {
        setUsers([])
      }

      timeout2 = setTimeout(() => {
        refFormik.current?.resetForm({
          bedrooms: arrBedrooms,
          justification: ''
        })
      }, 500)
    }, 500)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, item])

  useEffect(() => {
    setUsers([...request.request_user_data])
    return () => {
      setUsers([])
    }
  }, [request.request_user_data, visible])

  function addUser(index: number, user: RequestUserData) {
    const arrBedrooms: Bedroom[] = [...refFormik.current.state.values.bedrooms]

    arrBedrooms[index].users.push(user)

    refFormik.current.setFieldValue('bedrooms', arrBedrooms)

    setUsers(
      users.filter(
        userItem => userItem.traveler.user_id !== user.traveler.user_id
      )
    )
  }

  function removeUser(index: number, user: RequestUserData) {
    const arrBedrooms: Bedroom[] = [...refFormik.current.state.values.bedrooms]

    arrBedrooms[index].users = arrBedrooms[index].users.filter(
      userItem => userItem.traveler.user_id !== user.traveler.user_id
    )

    refFormik.current.setFieldValue('bedrooms', arrBedrooms)

    setUsers([...users, user])
  }

  function handleSubmit(
    values: SelectedHotel,
    formikActions: FormikActions<SelectedHotel>
  ) {
    const fullRequestHotelSource = onSubmit
      ? selectedHotels
      : request.full_request_hotel
    let customer_supplier_id: Id

    const budgetHotelTickets: BudgetChartTicket[] = []
    const rooms = values.bedrooms.map(bedroomsItem => {
      const selectedRoom = item.request_quote_hotel_rooms.find(
        roomFilter =>
          roomFilter.request_quote_hotel_room_hash === bedroomsItem.price
      )

      customer_supplier_id = selectedRoom.customer_supplier_id
      budgetHotelTickets.push({
        standard_type: 'HOTEL',
        total_value: selectedRoom.total_value
      })

      return {
        guests: bedroomsItem.users.map((userItem, userItemIndex) => ({
          owner: userItemIndex === 0,
          user_id: userItem.user_id || userItem.traveler.user_id
        })),
        request_quote_hash: selectedRoom.request_quote_hash,
        request_quote_hotel_room_hash:
          selectedRoom.request_quote_hotel_room_hash,
        request_quote_hotel_route_hash: selectedRoom.request_quote_route_hash,
        request_quote_hotel_unique: selectedRoom.request_quote_hotel_unique
      }
    }) as FullRequestHotelRooms[]

    const full_request_hotel = [
      ...fullRequestHotelSource.filter(
        ({ message_id }) =>
          !message_id.find(message =>
            searches[currentPeriodKey].message_id.includes(message)
          )
      ),
      {
        customer_supplier_id,
        justification: [values.justification],
        message_id: [...searches[currentPeriodKey].message_id],
        request_quote_hotel_hash: item.request_quote_hotel_hash,
        rooms
      }
    ] as FullRequestHotel[]

    notification.info({
      message: `${t('web.services.hotels.title')} ${t(
        'common.actions.selected'
      )}`,
      placement: 'bottomRight'
    })

    dispatch(setHotelsNextPeriodKey())

    if (onSubmit) {
      if (full_request_hotel.length === Object.keys(searches).length) {
        onSubmit(full_request_hotel)
        setSelectedHotels([])
        return
      }
      setSelectedHotels(full_request_hotel)
      toggle()
      return
    }
    setSelectedHotels(full_request_hotel)

    stepsContext.setBudgetAllocations({
      standard_type: 'HOTEL',
      tickets: budgetHotelTickets
    })

    stepsContext.setRequest({
      ...stepsContext.request,
      full_request_hotel
    })

    toggle()
  }

  return (
    <Modal
      cancelButtonProps={{ loading }}
      forceRender
      okButtonProps={{
        //@ts-ignore
        'data-test-id': 'hotel-result-modal-action',
        loading
      }}
      onCancel={() => {
        toggle()
        refFormik.current.resetForm(initialValues)
      }}
      onOk={async () => {
        const hasErrors = await refFormik.current.validateForm(
          refFormik.current.state.values
        )
        if (hasErrors.bedrooms) {
          refFormik.current.submitForm()
          return
        }
        if (
          policyRule?.policy_request_hotel_rule?.active &&
          policyRule?.policy_request_hotel_rule?.lowest_fare
        ) {
          const hasSelectedMinFare =
            refFormik.current.state.values.bedrooms.every(bedroom => {
              if (bedroom.users.length) {
                return item.request_quote_hotel_rooms.find(
                  room => room.request_quote_hotel_room_hash === bedroom.price
                ).min_room
              }
              return true
            })
          if (!hasSelectedMinFare) {
            switch (policyRule?.policy_request_hotel_rule.action) {
              case 'ALERT':
                ModalAnt.confirm({
                  cancelText: <Text onlyText title="common.actions.cancel" />,
                  content: (
                    <>
                      <Text title="web.services.hotels.validation.policy.min-fare.alert.content" />
                      <Input
                        allowClear
                        name="minFareHotelJustification"
                        placeholder="web.services.hotels.validation.policy.min-fare.alert.justification.placeholder"
                        size="large"
                      />
                    </>
                  ),
                  okText: <Text onlyText title="common.actions.yes" />,
                  title: (
                    <Text
                      onlyText
                      title="web.services.hotels.validation.policy.min-fare.alert.title"
                    />
                  ),
                  onCancel() {},
                  onOk: async () => {
                    const justification: any = document.querySelector(
                      "input[name='minFareHotelJustification']"
                    )
                    if (justification?.value.trim().length) {
                      await refFormik.current.setFieldValue(
                        'justification',
                        justification.value
                      )
                      refFormik.current.submitForm()
                    }
                  }
                })
                break
              case 'BLOCK':
                ModalAnt.error({
                  content: (
                    <Text
                      onlyText
                      title="web.services.hotels.validation.policy.min-fare.block.content"
                    />
                  ),
                  onOk() {},
                  okText: <Text onlyText title="common.actions.close" />,
                  title: (
                    <Text
                      onlyText
                      title="web.services.hotels.validation.policy.min-fare.block.title"
                    />
                  )
                })
            }
          } else {
            refFormik.current.submitForm()
          }
        } else {
          refFormik.current.submitForm()
        }
      }}
      title={<>{item ? item.brand_name : ''}</>}
      visible={visible}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => (
          <>
            {!!users.length && (
              <>
                <Title
                  level={4}
                  title={JSON.stringify({
                    message: 'web.setup.participants.title',
                    params: { count: users.length }
                  })}
                />
                <DivUserList>
                  {users.map(userItem => (
                    <Tooltip
                      key={userItem.traveler.user_id}
                      title={userItem.traveler.name}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a>
                        <WrapAvatar
                          gender={userItem.traveler.gender}
                          name={userItem.traveler.name}
                          size="large"
                        />
                      </a>
                    </Tooltip>
                  ))}
                </DivUserList>
              </>
            )}
            {!!item &&
              formikProps.values.bedrooms.map((bedroomItem, bedroomIndex) => (
                <WrapListItem
                  key={bedroomItem.key}
                  integration_type={bedroomItem.integration_type}
                >
                  <DivCombo>
                    <Title level={4} title="web.services.hotels.form.bedroom">
                      {` ${bedroomItem.key}`}
                    </Title>
                    <div>
                      <Select
                        data-test-id={`hotel-result-modal-bedroom-list-${bedroomIndex}`}
                        onChange={(value: string) => {
                          const priceList = getPriceList(value)
                          formikProps.setFieldValue(
                            `bedrooms.${bedroomIndex}`,
                            {
                              ...formikProps.values.bedrooms[bedroomIndex],
                              category: value,
                              integration_type: priceList[0].integration_type,
                              max_adults: priceList[0].max_adults,
                              pension: priceList[0].pension,
                              price: priceList[0].request_quote_hotel_room_hash
                            }
                          )
                        }}
                        style={{ marginRight: 8, width: 200 }}
                        value={
                          formikProps.values.bedrooms[bedroomIndex].category
                        }
                      >
                        {categoryList.map((category, index) => (
                          <Select.Option
                            data-test-id={`hotel-result-modal-bedroom-item-${index}`}
                            key={category}
                            title={category}
                            value={category}
                          >
                            {category}
                          </Select.Option>
                        ))}
                      </Select>
                      <Select
                        data-test-id={`hotel-result-modal-value-list-${bedroomIndex}`}
                        onChange={(value: string) => {
                          const room = findRoomWithHash(value)
                          formikProps.setFieldValue(
                            `bedrooms.${bedroomIndex}`,
                            {
                              ...formikProps.values.bedrooms[bedroomIndex],
                              integration_type: room.integration_type,
                              max_adults: room.max_adults,
                              pension: room.pension,
                              price: value
                            }
                          )
                        }}
                        style={{ width: 100 }}
                        value={`${formikProps.values.bedrooms[bedroomIndex].price}`}
                      >
                        {!!bedroomItem.category &&
                          getPriceList(
                            formikProps.values.bedrooms[bedroomIndex].category
                          ).map((roomOffers, index) => (
                            <Select.Option
                              data-test-id={`hotel-result-modal-value-item-${index}`}
                              key={roomOffers.request_quote_hotel_room_hash}
                              title={`${roomOffers.total_value}`}
                              value={roomOffers.request_quote_hotel_room_hash}
                            >
                              {
                                CurrencyMask({
                                  formatStr: true,
                                  value: roomOffers.total_value
                                }).maskedValue
                              }
                              {roomOffers.pension && ' ☕️'}
                            </Select.Option>
                          ))}
                      </Select>
                    </div>
                  </DivCombo>
                  <DivRoomInfos>
                    {bedroomItem.integration_type === 'OFFLINE' && (
                      <OfflineOffer>
                        {t('web.services.hotels.offline-offer')}
                      </OfflineOffer>
                    )}
                    <div>
                      <IconPension type="coffee" />
                      {/* web.services.hotels.pension-included */}
                      {/* web.services.hotels.pension-not-included */}
                      {t(
                        `web.services.hotels.pension${
                          formikProps.values.bedrooms[bedroomIndex].pension
                            ? ''
                            : '-not'
                        }-included`
                      )}
                    </div>
                  </DivRoomInfos>
                  <WrapAbleUsers>
                    {bedroomItem.users.map(bedroomUserItem => (
                      <Tooltip
                        key={bedroomUserItem.traveler.user_id}
                        title={bedroomUserItem.traveler.name}
                      >
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a
                          data-test-id={`hotel-result-modal-remove-user-${bedroomUserItem.user_id}`}
                          onClick={() =>
                            removeUser(bedroomIndex, bedroomUserItem)
                          }
                        >
                          <WrapAvatar
                            gender={bedroomUserItem.traveler.gender}
                            name={bedroomUserItem.traveler.name}
                            size="large"
                          />
                        </a>
                      </Tooltip>
                    ))}
                    <Popover
                      content={
                        <>
                          {users.map(userItem => (
                            <Tooltip
                              key={userItem.traveler.user_id}
                              title={userItem.traveler.name}
                            >
                              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                              <a
                                data-test-id={`hotel-result-modal-add-user-${userItem.user_id}`}
                                onClick={() => addUser(bedroomIndex, userItem)}
                              >
                                <WrapAvatar
                                  gender={userItem.traveler.gender}
                                  name={userItem.traveler.name}
                                  size="large"
                                />
                              </a>
                            </Tooltip>
                          ))}
                        </>
                      }
                      trigger="click"
                    >
                      <Button
                        data-test-id="hotel-result-modal-add-user"
                        disabled={!users.length}
                        icon="plus"
                        shape="circle"
                      />
                    </Popover>
                  </WrapAbleUsers>
                  {!!formikProps.submitCount &&
                    !!formikProps.errors.bedrooms &&
                    !!formikProps.errors.bedrooms[bedroomIndex] && (
                      <Alert
                        message={
                          formikProps.errors.bedrooms[bedroomIndex].users
                        }
                      />
                    )}
                </WrapListItem>
              ))}
          </>
        )}
        validationSchema={object().shape({
          bedrooms: array().of(
            object().shape({
              users: array()
                .test(
                  'test whether all guests are allocated',
                  'web.services.hotels.selection-form.users.error-user-not-selected',
                  () => !users.length
                )
                .min(1, 'web.services.hotels.selection-form.users.error-min')
            })
          )
        })}
      />
    </Modal>
  )
}

export default ModalSelectedHotel
